import { isEmpty, get, reject } from "lodash";
import { useState } from "react";

import { useTagFormValidator } from "src/hooks/useTagFormValidator";
import { useAttributesUpdater } from "src/components/TagsEditor/sub-components/CreateNewTagModal/hooks/useAttributesUpdater";
import {
  newTagPayload,
  validationErrorForCreateSave,
} from "./createNewTagModal.save";
import { useCreateNewTagModalMeta } from "./useCreateNewTagModal.meta";

export const getTagTypeOptions = (allTags) => {
  return reject(allTags, (tagType) => tagType.isReadonly).map(
    ({ id, name }) => ({
      label: name || id,
      value: id,
    })
  );
};

export const selectedTagOption = (selectedTag) => {
  if (!selectedTag) {
    return null;
  }
  const { id, name } = selectedTag;
  return {
    value: id,
    label: name || id,
  };
};

export const useCreateNewTagModal = ({
  onClose,
  allTags,
  setNewTag,
  initialDisplayValue,
  initialTagType,
  initialAttributes,
  entityId,
}) => {
  const [displayValue, setDisplayValue] = useState(initialDisplayValue || "");
  const [selectedTag, setSelectedTag] = useState(initialTagType || null);
  const { updatedAttributes, setUpdatedAttributes } = useAttributesUpdater({
    entityId,
    initialAttributes,
    selectedTag,
  });

  const {
    onFieldUpdate,
    onChangeTagType,
    onFormSave,
    fieldToBeFocused,
    fieldsToShowInvalidity,
    clearFieldToBeFocused,
  } = useTagFormValidator({
    fields: {},
    tagType: selectedTag,
    tagValue: { attributes: updatedAttributes },
    includeEntityAttributes: true,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState<Error | null>(null);
  const meta = useCreateNewTagModalMeta({
    selectedTag,
    updatedAttributes,
    entityId,
    setDisplayValue,
  });

  const applyAttributePatch = (id, value) => {
    const newAttributes = {
      ...updatedAttributes,
      [id]: value,
    };
    clearFieldToBeFocused();
    onFieldUpdate(newAttributes);
    setUpdatedAttributes(newAttributes);
    setValidationError(null);
  };

  const onSave = async () => {
    const nextError = validationErrorForCreateSave({
      invalidFields: onFormSave({ attributes: updatedAttributes }),
      selectedTag,
      displayValue,
    });
    if (nextError) {
      setValidationError(nextError);
      return;
    }

    try {
      setIsSaving(true);
      await setNewTag(
        newTagPayload(displayValue, updatedAttributes),
        selectedTag
      );
      setIsSaving(false);
      onClose();
    } catch (e) {
      setError(e);
      setIsSaving(false);
    }
  };

  const handleTagTypeChange = ({ value }) => {
    setUpdatedAttributes({});
    setDisplayValue("");
    setIsSaving(false);
    setError(null);
    setValidationError(null);
    const selectedTagType = allTags.find((tagType) => tagType.id === value);
    setSelectedTag(selectedTagType);
    onChangeTagType(selectedTagType);
  };

  return {
    onSave,
    isSaving,
    validationError,
    error,
    selectedTag,
    tagTypeOptions: getTagTypeOptions(allTags),
    handleTagTypeChange,
    getSelectedTagOption: () => selectedTagOption(selectedTag),
    hasNoValueAttrs: isEmpty(get(selectedTag, "attributes.values")),
    displayValue,
    onDisplayValueChange: (e) => setDisplayValue(e.target.value),
    ...meta,
    updatedAttributes,
    onAttrValueChange: (type) => (e) =>
      applyAttributePatch(type, e.target.value),
    onSelectAttrValue: (id) => (option) =>
      applyAttributePatch(id, option ? option.value : undefined),
    onDateChange: (id) => (value) => applyAttributePatch(id, value),
    fieldToBeFocused,
    fieldsToShowInvalidity,
  };
};
